import axios from 'axios';
import * as S from '../auth.styles';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams } from 'react-router';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setPassword } from '../../../actions/changePassword';
import { push } from 'react-router-redux';
import { PasswordField } from '@design-system/molecules/password-field';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Fragment, useEffect, useState } from 'react';
import { Form } from '@design-system/molecules/form';
import { createPasswordSchema } from '@design-system/molecules/password-field';
import { appConfig } from '../../../config';

const createVerificationSchema = (intl: IntlShape) => {
  return z.object({
    newPassword: createPasswordSchema({
      required: intl.formatMessage({ id: 'form.validation.required' }),
      minLength: intl.formatMessage({ id: 'form.validation.password' }),
      uppercaseLetter: intl.formatMessage({ id: 'form.validation.password' }),
      number: intl.formatMessage({ id: 'form.validation.password' }),
      specialCharacter: intl.formatMessage({ id: 'form.validation.password' }),
    }),
  });
};

type FormData = z.infer<ReturnType<typeof createVerificationSchema>>;

const FormContent = () => {
  const intl = useIntl();

  const { watch } = useFormContext<FormData>();

  const newPassword = watch('newPassword');

  return (
    <Fragment>
      <PasswordField
        name="newPassword"
        label={intl.formatMessage({ id: 'dialog.change.set.password.label.new.pass' })}
        placeholder="••••••••••••••"
        hintConfig={{ capslockOn: intl.formatMessage({ id: 'common.inputHint.password' }) }}
      />
      <S.BackupSpace />
      <S.AuthButton type="submit" variant="primary" size="large" isDisabled={!newPassword?.trim()}>
        <FormattedMessage id="common.button.confirm" />
      </S.AuthButton>
    </Fragment>
  );
};

export const UserVerification = () => {
  const intl = useIntl();
  const { hash } = useParams<{ hash: string }>();
  const dispatch = useDispatch();

  const [userName, setUserName] = useState('');

  const schema = createVerificationSchema(intl);

  useEffect(() => {
    axios.get(`${appConfig.URL_REST}users/new/${hash}`).then((res) => {
      if (res.data === '') {
        return dispatch(push('/login'));
      }
      setUserName(res.data.email);
    });
  }, [dispatch, hash]);

  const handleSubmit = (data: FormData) => {
    const { newPassword } = data;
    dispatch(setPassword(hash, newPassword, undefined));
  };

  return (
    <Form onSubmit={handleSubmit} config={{ mode: 'onBlur', resolver: zodResolver(schema) }}>
      <S.AuthContainer>
        <S.AuthHeader variant="h2" as="h2">
          <FormattedMessage id="login.set.password.dialog.title" values={{ userName }} />
          <S.AuthHeaderGreen as="span" variant="h2">
            {userName}
          </S.AuthHeaderGreen>
        </S.AuthHeader>
        <FormContent />
      </S.AuthContainer>
    </Form>
  );
};
